import { AutoFixHighRounded, TipsAndUpdatesRounded } from '@mui/icons-material'
import { Chip, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tabs, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import { purple } from '@mui/material/colors'
import _ from 'lodash'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IClaimFilter } from '../../definitions/Claims'
import { IAttributedMembersFilter } from '../../definitions/Members'
import Formatter from '../../helpers/Formatter'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import ActivityManager from '../../managers/ActivityManager'
import DataframeManager from '../../managers/DataframeManager'
import QualityMeasureManager from '../../managers/QualityMeasureManager'
import { useAppSelector } from '../../store/Hooks'
import AttributedMembersTable from '../AttributedMembersTable'
import ClaimsTable from '../ClaimsTable'
import { HorizontalBarChart, LineChart } from '../charts'
import BulletChart from '../charts/BulletChart'
import Mark from '../controls/Mark'
import RouteHeader from '../controls/RouteHeader'
import HighlightCard from '../controls/cards/HighlightCard'
import SmartCard from '../controls/cards/SmartCard'
import ChartCard from '../controls/data_visualization/ChartCard'
import InsightCard from '../controls/data_visualization/InsightCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import RoutingManager from '../routing/Routing'

export default function ProviderDetails() {
    const params = RoutingManager.getParams<{ providerId: number, contractId: number }>(useAppSelector(s => s.session.routeParams))
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [selectedTab, setSelectedTab] = useState<'quality-measures' | 'claims' | 'members'>('quality-measures')
    const theme = useTheme()
    const claimFilter: IClaimFilter = {
        memberIds: [],
        patientIds: [],
        providerIds: [params.providerId],
        statusIds: []
    }

    const membersFilter: IAttributedMembersFilter = {
        providerIds: [params.providerId]
    }

    const { status, response } = useQuery({
        queryKey: buildQueryKey('provider-details-', params.providerId, params.contractId),
        queryFn: async () => await ApiClient.getProviderDetails(params.providerId, params.contractId, time),
        cache: true
    })
    const isLoading = status === 'loading'

    const renderQualityMeasures = () => {
        return (
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Members</TableCell>
                        <TableCell>Band</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(response?.qualityMeasures ?? []).map(qm => {
                        const numerator = _.last(qm.overTime.numerator.points).y
                        const denominator = _.last(qm.overTime.denominator.points).y
                        const score = _.last(qm.overTime.score.points).y
                        const isInverse = qm.scoringBands.some(x => x.inverse)
                        const first = { name: '', fixedWidth: 10 }
                        const bands = isInverse ? [...qm.scoringBands, first] : [first, ...qm.scoringBands]

                        const currentBand = qm.scoringBands.find(x => x.isActive)
                        return (
                            <TableRow key={qm.id}>
                                <TableCell width='20%'>
                                    {qm.name}
                                </TableCell>
                                <TableCell>
                                    <BulletChart value={score / 100} isInverse={isInverse} bands={bands} />
                                </TableCell>
                                <TableCell width='10%'>
                                    {numerator}
                                    /
                                    {denominator}
                                </TableCell>
                                <TableCell width='15%'>
                                    {currentBand?.name ?? 'unqualified'}
                                    {' '}
                                    (+
                                    {currentBand?.gainPercentage ?? 0}
                                    %)
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                            {Formatter.number(_.sum(response?.qualityMeasures.map(x => x.scoringBands?.find(k => k.isActive)?.gainPercentage ?? 0)))}
                            %
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        )
    }

    const renderNarratives = () => {
        const worstQM = QualityMeasureManager.worst(response?.qualityMeasures)
        if (!worstQM) return null

        const currentBand = QualityMeasureManager.currentBand(worstQM)
        const topBand = QualityMeasureManager.topBand(worstQM)
        const loss = topBand.gainPercentage - (currentBand?.gainPercentage ?? 0)
        const monetaryLoss = _.round(DataframeManager.sumAll(response.incentives.baseGain) * loss / 100, 2)
        return (
            <SmartCard flex={0.5}>
                <Typography variant='overline' fontWeight='bold'>Quality Measure Score</Typography>
                <Typography fontWeight='bold' fontSize='1.125rem'>
                    Your worst offender is
                    {worstQM.name}
                </Typography>
                <Typography variant='body2'>
                    Your current band is
                    <i>{currentBand?.name ?? 'unqualified'}</i>
                    {' '}
                    losing you
                    <strong>
                        {loss}
                        %
                    </strong>
                    {' '}
                    on the final settlement.
                </Typography>
                <Typography variant='body2'>
                    This quality measure alone is costing you
                    <Mark>
                        $
                        {Formatter.number(monetaryLoss)}
                    </Mark>
                    .
                </Typography>
                <Box display='flex' flexDirection='row' mt={2} gap={1} alignItems='center'>
                    <Chip label='Next Steps' size='small' color='info' />
                    <Typography variant='body2'>
                        See the members in
                        <strong className='simple-text-link'>this population here ↗</strong>
                        {' '}
                        and schedule appointments.
                    </Typography>
                </Box>
            </SmartCard>
        )
    }

    const actualPMPM = DataframeManager.average(response?.actualPMPM)
    const targetPMPM = DataframeManager.average(response?.targetPMPM)

    const actualBudget = DataframeManager.sumAll(response?.incentives?.totalPayment)
    const targetBudget = DataframeManager.sumAll(response?.incentives?.assignedRevenue)

    const accruedLoss = DataframeManager.sumAll(response?.incentives?.lossMonetaryDF)

    const currentGainPercentage = response?.incentives?.currentGainPercentage
    const currentLossGainPercentage = response?.incentives?.currentLossGainPercentage

    return (
        <Tile.Container>
            <RouteHeader
                isLoading={isLoading}
                primary={response?.name}
                timeSettings={time}
                subActions={[
                    { label: 'Updated last week', icon: 'clock', onClick: () => ActivityManager.openActivity('providers') }
                ]}
            />
            <Tile.Row flex={0} spacing={2}>
                <SmartCard flex={1}>
                    <KPICard
                        isLoading={isLoading}
                        data={Helper.dataframeToKPICard(response?.compositeScore)}
                        unit='%'
                        label='Quality Measure avg. Score'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        isLoading={isLoading}
                        data={Helper.dataframeToKPICard(response?.members)}
                        label='Attributed Members'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        isLoading={isLoading}
                        data={Helper.dataframeToKPICard(response?.costOfCare)}
                        unit='$'
                        label='Total Claims ($)'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        isLoading={isLoading}
                        inverse
                        unit='$'
                        data={Helper.dataframeToKPICard(response?.actualPMPM)}
                        label='Actual PMPM'
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row flex={0}>
                <HighlightCard flex={1} style={{ gap: 16, marginRight: theme.spacing(-1), marginLeft: theme.spacing(-1) }}>
                    <Box display='flex' flexDirection='row' alignItems='center'>
                        <TipsAndUpdatesRounded fontSize='inherit' sx={{ mr: 1, color: 'orange' }} />
                        <Typography variant='caption' fontWeight='bold'>Found&nbsp;</Typography>
                        <Typography variant='caption' fontWeight='bold' color='orange' fontStyle='italic'>4 insights</Typography>
                    </Box>
                    <Tile.Row spacing={2}>
                        <SmartCard flex={1} className='animate delay-5'>
                            <InsightCard
                                title='Accrued Loss'
                                body={(
                                    <>
                                        Accrued Loss is around
                                        <Mark>
                                            $
                                            {Formatter.number(accruedLoss)}
                                        </Mark>
                                        {' '}
                                        due to
                                        <strong>low</strong>
                                        {' '}
                                        quality measure scores.
                                    </>
                                )}
                                isLoading={isLoading}
                                valuation={accruedLoss <= 0}
                                chartData={{
                                    datasets: [{ data: DataframeManager.getYs(response?.incentives?.lossMonetaryDF), label: 'Loss', unit: '%' }],
                                    labels: DataframeManager.getXs(response?.incentives?.lossMonetaryDF)
                                }}
                            />
                        </SmartCard>
                        <SmartCard flex={1} className='animate delay-6'>
                            <InsightCard
                                title='Target vs Actual'
                                body={(
                                    <>
                                        Your actual spend is
                                        <Mark>
                                            $
                                            {Formatter.compact(actualBudget)}
                                        </Mark>
                                        {' '}
                                        compared to your allocated budget of
                                        <strong>
                                            $
                                            {Formatter.compact(targetBudget)}
                                        </strong>
                                        .
                                    </>
                                )}
                                isLoading={isLoading}
                                valuation={actualBudget < targetBudget}
                                chartData={{
                                    datasets: [
                                        { data: DataframeManager.getYs(response?.incentives?.totalPayment), label: 'Spend', unit: '$' },
                                        { data: DataframeManager.getYs(response?.incentives?.assignedRevenue), label: 'Budget', unit: '$' }
                                    ],
                                    labels: DataframeManager.getXs(response?.incentives?.totalPayment)
                                }}
                            />
                        </SmartCard>
                        <SmartCard flex={1} className='animate delay-7'>
                            <InsightCard
                                title='Quality Measures'
                                body={(
                                    <>
                                        Your current gain percentage is
                                        <Mark>
                                            {Formatter.number(currentGainPercentage)}
                                            %
                                        </Mark>
                                        , off a maximum of
                                        <strong>{Formatter.number(_.max(response?.incentives?.lossGainPercentageDF?.points?.map(x => x.y)))}</strong>
                                        .
                                    </>
                                )}
                                isLoading={isLoading}
                                valuation={currentLossGainPercentage <= 0}
                                chartData={{
                                    datasets: [{ data: DataframeManager.getYs(response?.incentives?.lossGainPercentageDF), label: 'Loss', unit: '%' }],
                                    labels: DataframeManager.getXs(response?.incentives?.lossGainPercentageDF)
                                }}
                            />
                        </SmartCard>
                        <SmartCard flex={1} className='animate delay-8'>
                            <InsightCard
                                title='PMPMs'
                                body={(
                                    <>
                                        On average the PMPM is
                                        <Mark>
                                            $
                                            {Formatter.compact(actualPMPM)}
                                        </Mark>
                                        {' '}
                                        compared to your average targetPMPM of
                                        <strong>
                                            $
                                            {Formatter.compact(targetPMPM)}
                                        </strong>
                                        .
                                    </>
                                )}
                                isLoading={isLoading}
                                valuation={actualPMPM < targetPMPM}
                                chartData={{
                                    datasets: [
                                        { data: DataframeManager.getYs(response?.actualPMPM), label: 'Actual', unit: '%' },
                                        { data: DataframeManager.getYs(response?.targetPMPM), label: 'Target', unit: '%' }
                                    ],
                                    labels: DataframeManager.getXs(response?.incentives?.totalPayment)
                                }}
                            />
                        </SmartCard>
                    </Tile.Row>
                </HighlightCard>
            </Tile.Row>
            <Tile.Row spacing={2}>
                <SmartCard flex={1} rows={4}>
                    <ChartCard
                        title='Quality Measures'
                        chart={(
                            <LineChart
                                isLoading={isLoading}
                                data={{
                                    datasets: (response?.qualityMeasures ?? [])?.map(x => {
                                        return {
                                            data: x.overTime.score.points.map(x => x.y),
                                            label: _.truncate(x.name, { length: 20 }),
                                            unit: '%'
                                        }
                                    }),
                                    labels: (response?.qualityMeasures[0]?.overTime?.score?.points ?? [])?.map(x => x.x)
                                }}
                            />
                        )}
                    />
                </SmartCard>
                <SmartCard flex={1} rows={4}>
                    <ChartCard
                        title='Members at PMPM Threshold'
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                options={{
                                    plugins: {
                                        datalabels: { display: false },
                                        legend: { display: false },
                                        annotation: {
                                            annotations: {
                                                line1: {
                                                    type: 'line',
                                                    xMin: response?.stoploss ?? 0,
                                                    xMax: response?.stoploss ?? 0,
                                                    borderColor: theme.palette.error.main,
                                                    borderWidth: 5
                                                }
                                            }
                                        }
                                    }
                                }}
                                data={{
                                    datasets: [
                                        { data: response?.topExpensesMember?.sort(x => x.totalspent)?.map(x => x.totalspent) }
                                    ],
                                    labels: response?.topExpensesMember?.sort(x => x.totalspent)?.map(x => 'member-' + x.memberid, { length: 20 })
                                }}
                            />
                        )}
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row spacing={2}>
                <HighlightCard flex={1} style={{ gap: 16, marginRight: theme.spacing(-1), marginLeft: theme.spacing(-1) }}>
                    <Box display='flex' flexDirection='row' alignItems='center'>
                        <AutoFixHighRounded fontSize='inherit' sx={{ mr: 1, color: purple[500] }} />
                        <Typography variant='caption' fontWeight='bold'>Found&nbsp;</Typography>
                        <Typography variant='caption' fontWeight='bold' color={purple[500]} fontStyle='italic'>1 narrative</Typography>
                    </Box>
                    <Box display='flex' flexDirection='row' gap={2}>
                        {renderNarratives()}
                    </Box>
                </HighlightCard>
            </Tile.Row>
            <Tile.Tile flex={1}>
                <SmartCard flex={1}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} aria-label='basic tabs example'>
                            <Tab disabled={isLoading} value='quality-measures' label='Quality Measures' />
                            <Tab disabled={isLoading} value='claims' label='Claims' />
                            <Tab disabled={isLoading} value='members' label='Members' />
                        </Tabs>
                    </Box>
                    <Box padding={1}>
                        {selectedTab === 'quality-measures'
                            ? renderQualityMeasures()
                            : selectedTab === 'claims'
                                ? (
                                        <div style={{ minHeight: '5rem' }}>
                                            <ClaimsTable contractId={params.contractId} totalClaims={_.max(response?.totalClaims.points.map(x => x.y)) ?? 0} filter={claimFilter} />
                                        </div>
                                    )
                                : (
                                        <div style={{ minHeight: '5rem' }}>
                                            <AttributedMembersTable contractId={params.contractId} filter={membersFilter} />
                                        </div>
                                    )}
                    </Box>
                </SmartCard>
            </Tile.Tile>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
